import { Box, Button, Popup, Table, TableRow, TopBar } from '@sam/components';
import { TableHeader, TableKey } from '@sam/components/src/Table/Table.types';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  deleteSchambeckUser,
  downloadFile,
  getAllUsersWithoutOffice,
  getUserOverviewCSV,
  Language,
  SchambeckUser,
  updateSchambeckUserTableConfig,
  useData,
} from 'shared';
import { Right } from 'shared/src/userRole/UserRole.types';
import { NotAllowed } from '../../components/NotAllowed';
import { useUser } from '../../components/UserContext';
import { convertUsersIntoTableEntries, isUserAllowedToDo } from '../../utils/user/User.utils';
import { IconComponent, IconType } from '../../components/icons/IconComponent';

const UserOverviewPage: React.FC = () => {
  const [userToDeleteId, setUserToDeleteId] = useState<string>();
  const { axios, user, updateContextUser } = useUser();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [filterValue, setFilterValue] = useState<string>('');
  const [showDownloadPopup, toggleShowDownloadPopup] = useState<boolean>(false);
  const [includeDeactivated, toggleIncludeDeactivated] = useState<boolean>(false);
  const [usersWithoutOffice, setUsersWithoutOffice] = useState<SchambeckUser[]>([]);
  const [showUnassigned, toggleShowUnassigned] = useState<boolean>(false);
  const { data: loadedUsers, mutate: updateUser } = useData('SCHAMBECK_USERS_ALL', {
    config: { fallbackData: [] },
  });
  const { data: loadedActiveUsers } = useData('SCHAMBECK_USERS_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });
  const { data: loadedUserFunctions } = useData('USER_FUNCTION_ALL', {
    config: { fallbackData: [] },
  });
  const { data: loadedOffices } = useData('OFFICES_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });
  const { data: loadedWorkQualifications } = useData('QUALIFICATIONS_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });
  /**
   * Used to check if warning for unassigned users should be shown
   */
  const isAdmin: boolean = useMemo(
    (): boolean =>
      user.right.customRights.get(Right.ALL) || user.right.role.rights.includes(Right.ALL),
    [user],
  );

  /**
   * Hook to load all users without an assignedOffice
   */
  useEffect(() => {
    if (!isAdmin || !axios) return;
    getAllUsersWithoutOffice(axios).then(setUsersWithoutOffice);
  }, [axios, isAdmin]);
  /**
   * Helper Method to convert users to tableRows
   */
  const rows: TableRow[] = useMemo((): TableRow[] => {
    const usersToDisplay: SchambeckUser[] = showUnassigned
      ? usersWithoutOffice
      : includeDeactivated
        ? loadedUsers
        : loadedActiveUsers;
    return convertUsersIntoTableEntries(
      user.right,
      usersToDisplay,
      loadedUserFunctions,
      loadedOffices,
      navigate,
      setUserToDeleteId,
      loadedWorkQualifications,
    );
  }, [
    includeDeactivated,
    loadedActiveUsers,
    usersWithoutOffice,
    loadedOffices,
    loadedUserFunctions,
    loadedUsers,
    loadedWorkQualifications,
    navigate,
    showUnassigned,
    user.right,
  ]);

  /**
   * Helper method to crete and download the overview csv
   * @param type short creates a csv with just the number and name
   */
  const handleCsvDownload = (type: 'short' | 'full'): void => {
    getUserOverviewCSV(axios, type, user.language || Language.DE).then(
      (generatedFile) =>
        generatedFile && downloadFile(generatedFile, t('pages.user.overview.overviewFilename')),
    );
  };

  return isUserAllowedToDo(user.right, [
    Right.USER_VIEW_ALL,
    Right.USER_EDIT,
    Right.USER_CREATE,
  ]) ? (
    <>
      <TopBar title={t('pages.user.overview.headline')} onSearch={setFilterValue}>
        {isUserAllowedToDo(user.right, [Right.USER_CREATE]) && (
          <>
            <IconComponent type={IconType.ADD} onClick={() => navigate('/user/create')} />
          </>
        )}
        <IconComponent
          type={IconType.DISABLED}
          style={{ color: includeDeactivated ? '#bc2e46' : '' }}
          onClick={() => toggleIncludeDeactivated(!includeDeactivated)}
        />
        <IconComponent type={IconType.DOWNLOAD} onClick={() => toggleShowDownloadPopup(true)} />
        {isAdmin && usersWithoutOffice.length > 0 && (
          <IconComponent
            className="clickable"
            type={IconType.EXCLAMATION}
            style={{ color: '#bc2e46' }}
            onClick={() => toggleShowUnassigned(!showUnassigned)}
          />
        )}
      </TopBar>
      <Popup
        isOpen={showDownloadPopup}
        title={t('pages.user.overview.filePopup')}
        onClose={() => toggleShowDownloadPopup(false)}
        buttons={[
          <Button
            value={t('pages.user.overview.shortVersion')}
            onClick={() => handleCsvDownload('short')}
          />,
          <Button
            value={t('pages.user.overview.fullVersion')}
            onClick={() => handleCsvDownload('full')}
          />,
        ]}
      >
        <p>{t('pages.user.overview.downloadPopupText')}</p>
      </Popup>
      <Box>
        <Table
          filterValue={filterValue}
          rows={rows}
          header={
            t('pages.user.overview.tableHeader', {
              returnObjects: true,
            }) as TableHeader[]
          }
          tableConfig={user.tableConfig?.get(TableKey.USER_OVERVIEW)}
          updateTableConfig={(updatedHeader) => {
            updateSchambeckUserTableConfig(axios, user, TableKey.USER_OVERVIEW, updatedHeader).then(
              (updatedUser) => updatedUser && updateContextUser(updatedUser),
            );
          }}
        />
      </Box>
      <Popup
        isOpen={!!userToDeleteId}
        onClose={() => setUserToDeleteId(undefined)}
        title={t('pages.user.overview.deletePopup')}
        buttons={[
          <Button
            value={t('general.buttons.yes')}
            onClick={() =>
              deleteSchambeckUser(userToDeleteId!, axios).then((success) => {
                if (success) {
                  updateUser(
                    loadedUsers.filter((userToCheck) => userToCheck.id !== userToDeleteId),
                  );
                  setUserToDeleteId(undefined);
                }
              })
            }
          />,
          <Button value={t('general.buttons.no')} onClick={() => setUserToDeleteId(undefined)} />,
        ]}
      >
        <p>{t('general.popup.deleteUser')}</p>
      </Popup>
    </>
  ) : (
    <NotAllowed neccessaryRight={Right.USER_VIEW_ALL} />
  );
};
export default UserOverviewPage;
